// Mock data — mirrors what will live in Supabase tables once Phase 2 lands.
// Keep shape in sync with `src/types/`.
//
// Team roster is derived from `config/teams.json` — the single source of
// truth. Anyone adding / renaming / dropping a team edits that one file;
// the seed script (`npm run seed:event`) pushes it to Supabase, and this
// import picks it up at build time for the offline mock.
//
// Open follow-up: per-team check-in defaults (`initialCheckIn` below) and
// the per-robot inspection seed (`initialTeamRobotInspection`) are still
// hand-keyed by team id. Adding a brand-new team requires touching those
// dictionaries too, or the mock won't have a row for the team. Both will
// fall back to sensible defaults at runtime if missing, but ideally those
// should also auto-derive from teams.json (and `has_backup_robot` lives
// in the JSON so a generator could pick it up — left as a follow-up).
import type {
  Team, StatusMeta, MatchTeamStatus, Match, EventKind,
  ARCTICEntry, ECRun, Group3v3, Swiss1v1,
  ActiveInspection, QueueItem, Warning, TeamRobotInspection, PreInspectionLevel, CheckIn,
  FieldCalibration, TeamPictures, TeamConfig, GroupLetter, MatchEvent, PracticeMatch,
} from '../types';
import teamsConfigJson from '../../config/teams.json';

// JSON-import shape. TS infers literal-string types from the file, which
// would lock us to the current roster — re-cast through a wider shape so
// the file can grow / shrink without TS getting upset at the call sites.
type TeamConfigJsonEntry = {
  id: string;
  name: string;
  short: string;
  events: string[];
  robots: string[];
  group_3v3?: string;
  initial_seed_1v1?: number;
  has_backup_robot?: boolean;
};
type TeamConfigJson = { teams: TeamConfigJsonEntry[] };

const teamsConfigData = teamsConfigJson as unknown as TeamConfigJson;

export const TEAMS: Team[] = teamsConfigData.teams.map((t) => ({
  id: t.id,
  name: t.name,
  short: t.short,
  events: t.events as EventKind[],
  robots: t.robots,
  group: t.group_3v3,
}));

export const STATUS: Record<MatchTeamStatus, StatusMeta> = {
  not_tracked:     { label: 'Untracked',        tone: 'neutral' },
  at_inspection:   { label: 'At Inspection',    tone: 'info' },
  s1:              { label: 'At Inspection',    tone: 'info' },  // legacy
  s2:              { label: 'At Inspection',    tone: 'info' },  // legacy
  s3:              { label: 'At Inspection',    tone: 'info' },  // legacy
  l3:              { label: 'L3',               tone: 'info' },
  l2:              { label: 'L2',               tone: 'ok' },
  in_game:         { label: 'In Game',          tone: 'live' },
  completed:       { label: 'Completed',        tone: 'neutral' },
  forfeit_v:       { label: 'Forfeit (V)',      tone: 'err' },
  forfeit_iv:      { label: 'Forfeit (IV)',     tone: 'err' },
  rescheduled:     { label: 'Rescheduled',      tone: 'warn' },
  direct_transfer: { label: 'Direct Transfer',  tone: 'warn' },
};

export const STATUS_FLOW: MatchTeamStatus[] = [
  'not_tracked', 'at_inspection', 'l3', 'l2', 'in_game', 'completed',
];

type TeamId = string;

export const teamById = (id: TeamId): Team =>
  TEAMS.find((t) => t.id === id) ?? { id, name: id, short: id, events: [], robots: [] };

// First-paint mock — the supabase store overwrites these on fetchAll.
// Designed to look "mid-event" (Day 1, morning block): some completed, one
// live, one on-deck, rest upcoming. Timestamps come from packing into the
// default ARC schedule (3 days × 3 blocks per day). Used until SupabaseStore
// fetchAll lands; switching backend at runtime preserves these for tests.
import { DEFAULT_ARC_SCHEDULE, packMatchesIntoBlocks, resolveBlocks, formatScheduledAt } from '../state/schedule';

const MOCK_BLOCKS = resolveBlocks(DEFAULT_ARC_SCHEDULE);

// Helper: take a list of pairings + statuses + duration and emit Match[] with
// scheduledAt populated by the packer + display time derived for legacy reads.
function packAsMatches(
  rows: Array<Omit<Match, 'time' | 'scheduledAt'>>,
  durationMin: number,
): Match[] {
  const items = rows.map((r) => ({ id: r.id, durationMin }));
  const { assigned } = packMatchesIntoBlocks(items, MOCK_BLOCKS);
  const byId = new Map(assigned.map((a) => [a.id, a.scheduledAt]));
  return rows.map((r) => {
    const at = byId.get(r.id);
    return { ...r, scheduledAt: at, time: formatScheduledAt(at) };
  });
}

export const initialMatches3v3: Match[] = packAsMatches([
  { id: 'G1',  red: 'UW', blue: 'ACC', redS: 'completed',     blueS: 'completed' },
  { id: 'G2',  red: 'UTA', blue: 'CU',   redS: 'completed',     blueS: 'completed' },
  { id: 'G3',  red: 'SFU', blue: 'Purdue', redS: 'forfeit_v',     blueS: 'completed' },
  { id: 'G4',  red: 'Mac', blue: 'UBC', redS: 'in_game',       blueS: 'in_game', live: true },
  { id: 'G5',  red: 'UBC', blue: 'NYU', redS: 'l2',            blueS: 'l2',          onDeck: true },
  { id: 'G6',  red: 'MU', blue: 'UMN', redS: 'l3',            blueS: 'at_inspection' },
  { id: 'G7',  red: 'PolyMTL', blue: 'PoliTO', redS: 'at_inspection', blueS: 'at_inspection' },
  { id: 'G8',  red: 'QU', blue: 'GT', redS: 'at_inspection', blueS: 'not_tracked' },
  { id: 'G9',  red: 'OTU', blue: 'VT', redS: 'not_tracked',   blueS: 'not_tracked' },
  { id: 'G10', red: 'RHIT', blue: 'UTA',  redS: 'not_tracked',   blueS: 'not_tracked' },
  { id: 'G11', red: 'TAMU', blue: 'WashU', redS: 'not_tracked',   blueS: 'not_tracked' },
  { id: 'G12', red: 'RHIT', blue: 'UCSD', redS: 'not_tracked',   blueS: 'not_tracked' },
  { id: 'G13', red: 'UW', blue: 'MU', redS: 'not_tracked',   blueS: 'not_tracked' },
  { id: 'G14', red: 'ACC', blue: 'Purdue', redS: 'not_tracked',   blueS: 'not_tracked' },
  { id: 'G15', red: 'CU',   blue: 'SFU', redS: 'not_tracked',   blueS: 'not_tracked' },
], DEFAULT_ARC_SCHEDULE.matchDurationMinutes.qual_3v3);

export const initialMatches1v1: Match[] = packAsMatches([
  { id: 'E1', red: 'UW', blue: 'UBC', redS: 'completed',   blueS: 'completed' },
  { id: 'E2', red: 'UoN', blue: 'PolyMTL', redS: 'completed',   blueS: 'completed' },
  { id: 'E3', red: 'SFU', blue: 'QU', redS: 'in_game',     blueS: 'in_game', live: true },
  { id: 'E4', red: 'UCSB', blue: 'OTU', redS: 'l2',          blueS: 'l3', onDeck: true },
  { id: 'E5', red: 'Purdue', blue: 'VT', redS: 'at_inspection', blueS: 'at_inspection' },
  { id: 'E6', red: 'CU',   blue: 'GT', redS: 'not_tracked', blueS: 'not_tracked' },
  { id: 'E7', red: 'UCSB', blue: 'PoliTO', redS: 'not_tracked', blueS: 'not_tracked' },
  { id: 'E8', red: 'UTA', blue: 'NUU', redS: 'not_tracked', blueS: 'not_tracked' },
], DEFAULT_ARC_SCHEDULE.matchDurationMinutes.qual_1v1);

// Mirrors config/schedule.json's arctic matches. 6 talks, 2 per day across
// Jul 1-3; the dev list flattens them (the live schedule keeps the day via
// scheduled_at).
export const initialARCTIC: ARCTICEntry[] = [
  { id: 'A1', time: '12:15 PM', day: 'Jul 1', topic: 'Spectre Dev Board',       presenter: 'Niles Peter Villaverde', team: 'NYU' },
  { id: 'A2', time: '1:15 PM',  day: 'Jul 1', topic: 'Runtime Configs',         presenter: 'Safal Ghimire',          team: 'NYU' },
  { id: 'A3', time: '12:15 PM', day: 'Jul 2', topic: 'Nickdexer',               presenter: 'Nicholas Winsett',       team: 'UCSD' },
  { id: 'A4', time: '1:15 PM',  day: 'Jul 2', topic: 'LiPo Supercap CTL Board', presenter: 'Niles Peter Villaverde', team: 'NYU' },
  { id: 'A5', time: '12:15 PM', day: 'Jul 3', topic: 'Client UI',               presenter: 'Ethan Harden',           team: 'RHIT' },
  { id: 'A7', time: '1:15 PM',  day: 'Jul 3', topic: 'Robotics Simulation',     presenter: 'Keyush Attarde',         team: 'UCSD' },
];

export const initialEC: ECRun[] = [
  { id: 'EC1', time: '6:00 PM', day: 'Jul 4', team: 'UW', status: 'completed',     completed: true },
  { id: 'EC2', time: '6:20 PM', day: 'Jul 4', team: 'SFU', status: 'completed',     completed: true },
  { id: 'EC3', time: '6:40 PM', day: 'Jul 4', team: 'Purdue', status: 'in_game',    live: true },
  { id: 'EC4', time: '7:00 PM', day: 'Jul 4', team: 'Mac', status: 'not_tracked' },
  { id: 'EC5', time: '7:20 PM', day: 'Jul 4', team: 'NUU', status: 'not_tracked' },
  { id: 'EC6', time: '7:40 PM', day: 'Jul 4', team: 'VT', status: 'not_tracked' },
  { id: 'EC7', time: '8:00 PM', day: 'Jul 4', team: 'PoliTO', status: 'not_tracked' },
  { id: 'EC8', time: '8:20 PM', day: 'Jul 4', team: 'TAMU', status: 'not_tracked' },
];

// Mock standings — 4 groups of 6, mid-event (only some matches played).
// Production data computed from match_rounds via src/state/standings.ts.
// Group standings — frozen mid-event snapshot. Production: computed in
// `src/state/standings.ts` from match outcomes. This mock satisfies first-paint.
// op/dp are zero in production (scores aren't captured); kept as fields for
// type backward-compat. cumulativeScore + medianBuchholz drive tiebreakers.
export const initialGroups3v3: Group3v3[] = [
  { name: 'A', rows: [
    { rank: 1, team: 'UW', w: 2, l: 0, t: 0, op: 0, dp: 0, rp: 6, cumulativeScore: 9,  medianBuchholz: 12, advance: true },
    { rank: 2, team: 'Purdue', w: 1, l: 1, t: 0, op: 0, dp: 0, rp: 3, cumulativeScore: 4,  medianBuchholz: 9, advance: true },
    { rank: 3, team: 'CU',   w: 1, l: 1, t: 0, op: 0, dp: 0, rp: 3, cumulativeScore: 3,  medianBuchholz: 8 },
    { rank: 4, team: 'ACC', w: 0, l: 2, t: 0, op: 0, dp: 0, rp: 0, cumulativeScore: 0,  medianBuchholz: 11 },
    { rank: 5, team: 'SFU', w: 0, l: 2, t: 0, op: 0, dp: 0, rp: 0, cumulativeScore: 0,  medianBuchholz: 9 },
  ]},
  { name: 'B', rows: [
    { rank: 1, team: 'Mac', w: 2, l: 0, t: 0, op: 0, dp: 0, rp: 6, cumulativeScore: 9,  medianBuchholz: 8,  advance: true },
    { rank: 2, team: 'UMN', w: 1, l: 0, t: 1, op: 0, dp: 0, rp: 4, cumulativeScore: 6,  medianBuchholz: 7,  advance: true },
    { rank: 3, team: 'MU', w: 1, l: 1, t: 0, op: 0, dp: 0, rp: 3, cumulativeScore: 4,  medianBuchholz: 7 },
    { rank: 4, team: 'UBC', w: 1, l: 1, t: 0, op: 0, dp: 0, rp: 3, cumulativeScore: 3,  medianBuchholz: 8 },
    { rank: 5, team: 'NYU', w: 0, l: 2, t: 0, op: 0, dp: 0, rp: 0, cumulativeScore: 0,  medianBuchholz: 9 },
  ]},
  { name: 'C', rows: [
    { rank: 1, team: 'VT', w: 2, l: 0, t: 0, op: 0, dp: 0, rp: 6, cumulativeScore: 9,  medianBuchholz: 9,  advance: true },
    { rank: 2, team: 'PolyMTL', w: 1, l: 1, t: 0, op: 0, dp: 0, rp: 3, cumulativeScore: 5,  medianBuchholz: 9,  advance: true },
    { rank: 3, team: 'PoliTO', w: 1, l: 1, t: 0, op: 0, dp: 0, rp: 3, cumulativeScore: 4,  medianBuchholz: 9 },
    { rank: 4, team: 'QU', w: 1, l: 1, t: 0, op: 0, dp: 0, rp: 3, cumulativeScore: 4,  medianBuchholz: 8 },
    { rank: 5, team: 'OTU', w: 1, l: 1, t: 0, op: 0, dp: 0, rp: 3, cumulativeScore: 3,  medianBuchholz: 9 },
    { rank: 6, team: 'GT', w: 0, l: 2, t: 0, op: 0, dp: 0, rp: 0, cumulativeScore: 0,  medianBuchholz: 9 },
  ]},
  { name: 'D', rows: [
    { rank: 1, team: 'TAMU', w: 2, l: 0, t: 0, op: 0, dp: 0, rp: 6, cumulativeScore: 9,  medianBuchholz: 8,  advance: true },
    { rank: 2, team: 'UCSD', w: 1, l: 1, t: 0, op: 0, dp: 0, rp: 3, cumulativeScore: 4,  medianBuchholz: 8,  advance: true },
    { rank: 3, team: 'UTA',  w: 1, l: 1, t: 0, op: 0, dp: 0, rp: 3, cumulativeScore: 3,  medianBuchholz: 9 },
    { rank: 4, team: 'RHIT', w: 0, l: 2, t: 0, op: 0, dp: 0, rp: 0, cumulativeScore: 0,  medianBuchholz: 8 },
    { rank: 5, team: 'WashU', w: 0, l: 2, t: 0, op: 0, dp: 0, rp: 0, cumulativeScore: 0,  medianBuchholz: 9 },
  ]},
];

export const initialSwiss1v1: Swiss1v1 = {
  round: 3, totalRounds: 6,
  rows: [
    { rank: 1,  team: 'UW', w: 3, l: 0, t: 0, rp: 9, cumulativeScore: 18, medianBuchholz: 12, advance: true },
    { rank: 2,  team: 'Mac', w: 2, l: 1, t: 0, rp: 6, cumulativeScore: 14, medianBuchholz: 13, advance: true },
    { rank: 3,  team: 'Purdue', w: 2, l: 1, t: 0, rp: 6, cumulativeScore: 12, medianBuchholz: 12, advance: true },
    { rank: 4,  team: 'VT', w: 2, l: 1, t: 0, rp: 6, cumulativeScore: 11, medianBuchholz: 10, advance: true },
    { rank: 5,  team: 'TAMU', w: 2, l: 1, t: 0, rp: 6, cumulativeScore: 10, medianBuchholz: 10, advance: true },
    { rank: 6,  team: 'PolyMTL', w: 2, l: 1, t: 0, rp: 6, cumulativeScore: 8,  medianBuchholz: 8,  advance: true },
    { rank: 7,  team: 'CU',   w: 1, l: 2, t: 0, rp: 3, cumulativeScore: 5,  medianBuchholz: 12 },
    { rank: 8, team: 'UMN', w: 1, l: 2, t: 0, rp: 3, cumulativeScore: 4,  medianBuchholz: 11 },
  ],
};

// Mock active inspection — picked QKRT's Hero robot at S1. Inspection always
// opens to the mechanical bench (S1) so a fresh inspector lands at the start
// of the desk; reducer SELECT_QUEUE_ITEM later updates currentStation when
// the operator switches tabs.
export const initialActiveInspection: ActiveInspection = {
  team: 'QU',
  match: 'G8',
  robotClass: 'Hero',
  queueId: 'q-G8-QKRT-Hero',
  serial: 'QKRT-HRO-001',
  currentStation: 's1',
  cells: { s1: 'active', s2: 'pending', s3: 'pending' },
};

// Per-robot queue. Each row tracks ONE robot's journey through S1 → S2 → S3.
// A team has 1–3 entries depending on their event's robot lineup. Multiple
// stations can inspect different robots of the same team concurrently
// (e.g. QKRT's Hero is busy at S2 here while Standard already passed S2 and
// is awaiting S3). Mirrors the physical inspection process: each station
// handles one robot at a time, parallel across stations.
// Mock-only synthetic owners. The local user (the one running the app) maps
// to '__anon__' via the reducer's anonymous fallback; their existing claim
// gets released when they pick another row. Other-station claims use distinct
// owner ids so they model "other inspectors at other stations" — the per-
// user release logic correctly leaves them alone.
const MOCK_LOCAL_USER = '__anon__';
const MOCK_OTHER_S1   = 'mock-inspector-s1';
const MOCK_OTHER_S3   = 'mock-inspector-s3';
// Seed held claims as just-touched so they read as live (not stale) on load.
const MOCK_FRESH_CLAIM = new Date().toISOString();

export const initialQueue: QueueItem[] = [
  // QKRT (G8.red) — mid-flow: Standard cleared S1+S2 awaiting S3; Hero claimed at S2; Sentry just S1.
  { id: 'q-G8-QKRT-Standard', team: 'QU', event: '3v3', robotClass: 'Standard', match: 'G8', time: '9:45', passedStations: ['s1', 's2'], busyAtStation: null, claimedBy: null,            isBackup: false, skipped: false, failed: false, urgent: true },
  { id: 'q-G8-QKRT-Hero',     team: 'QU', event: '3v3', robotClass: 'Hero',     match: 'G8', time: '9:45', passedStations: ['s1'],       busyAtStation: 's2', claimedBy: MOCK_LOCAL_USER, claimedAt: MOCK_FRESH_CLAIM, isBackup: false, skipped: false, failed: false, urgent: true },
  { id: 'q-G8-QKRT-Sentry',   team: 'QU', event: '3v3', robotClass: 'Sentry',   match: 'G8', time: '9:45', passedStations: ['s1'],       busyAtStation: null, claimedBy: null,            isBackup: false, skipped: false, failed: false, urgent: true },
  // QKRT brings a backup Standard — must inspect every match it plays.
  { id: 'q-G8-QKRT-Backup',   team: 'QU', event: '3v3', robotClass: 'Standard', match: 'G8', time: '9:45', passedStations: [],            busyAtStation: null, claimedBy: null,            isBackup: true,  skipped: false, failed: false, urgent: true },
  // NAGO (G6.red) — fresh in queue, none of its 3 robots have started.
  { id: 'q-G6-NAGO-Standard', team: 'MU', event: '3v3', robotClass: 'Standard', match: 'G6', time: '9:15', passedStations: [], busyAtStation: null, claimedBy: null, isBackup: false, skipped: false, failed: false, urgent: true },
  { id: 'q-G6-NAGO-Hero',     team: 'MU', event: '3v3', robotClass: 'Hero',     match: 'G6', time: '9:15', passedStations: [], busyAtStation: null, claimedBy: null, isBackup: false, skipped: false, failed: false, urgent: true },
  { id: 'q-G6-NAGO-Sentry',   team: 'MU', event: '3v3', robotClass: 'Sentry',   match: 'G6', time: '9:15', passedStations: [], busyAtStation: null, claimedBy: null, isBackup: false, skipped: false, failed: false, urgent: true },
  // NRST (G6.blue) — Standard cleared S1, Hero failed S2 (needs re-fix), Sentry hasn't started.
  { id: 'q-G6-NRST-Standard', team: 'UMN', event: '3v3', robotClass: 'Standard', match: 'G6', time: '9:15', passedStations: ['s1'], busyAtStation: null, claimedBy: null, isBackup: false, skipped: false, failed: false, urgent: true },
  // NRST-Hero failed at S2 → all station progress wiped, robot re-enters
  // at S1 (per the FAIL semantics: a mech check failure invalidates
  // downstream wiring/software passes, so re-do all three).
  { id: 'q-G6-NRST-Hero',     team: 'UMN', event: '3v3', robotClass: 'Hero',     match: 'G6', time: '9:15', passedStations: [],     busyAtStation: null, claimedBy: null, isBackup: false, skipped: false, failed: true,  urgent: true },
  { id: 'q-G6-NRST-Sentry',   team: 'UMN', event: '3v3', robotClass: 'Sentry',   match: 'G6', time: '9:15', passedStations: [],     busyAtStation: null, claimedBy: null, isBackup: false, skipped: false, failed: false, urgent: true },
  // PLYS (G7.red) — fresh.
  { id: 'q-G7-PLYS-Standard', team: 'PolyMTL', event: '3v3', robotClass: 'Standard', match: 'G7', time: '9:30', passedStations: [], busyAtStation: null, claimedBy: null, isBackup: false, skipped: false, failed: false, urgent: true },
  { id: 'q-G7-PLYS-Hero',     team: 'PolyMTL', event: '3v3', robotClass: 'Hero',     match: 'G7', time: '9:30', passedStations: [], busyAtStation: null, claimedBy: null, isBackup: false, skipped: false, failed: false, urgent: true },
  { id: 'q-G7-PLYS-Sentry',   team: 'PolyMTL', event: '3v3', robotClass: 'Sentry',   match: 'G7', time: '9:30', passedStations: [], busyAtStation: null, claimedBy: null, isBackup: false, skipped: false, failed: false, urgent: true },
  // RTOR (G7.blue) — far along: Standard at S3 right now, Hero+Sentry passed S1+S2 awaiting S3.
  { id: 'q-G7-RTOR-Standard', team: 'PoliTO', event: '3v3', robotClass: 'Standard', match: 'G7', time: '9:30', passedStations: ['s1', 's2'], busyAtStation: 's3', claimedBy: MOCK_OTHER_S3, claimedAt: MOCK_FRESH_CLAIM, isBackup: false, skipped: false, failed: false, urgent: false },
  { id: 'q-G7-RTOR-Hero',     team: 'PoliTO', event: '3v3', robotClass: 'Hero',     match: 'G7', time: '9:30', passedStations: ['s1', 's2'], busyAtStation: null, claimedBy: null,            isBackup: false, skipped: false, failed: false, urgent: false },
  { id: 'q-G7-RTOR-Sentry',   team: 'PoliTO', event: '3v3', robotClass: 'Sentry',   match: 'G7', time: '9:30', passedStations: ['s1', 's2'], busyAtStation: null, claimedBy: null,            isBackup: false, skipped: false, failed: false, urgent: false },
  // BOIL (E5.red) — 1v1 has just one robot (Standard); cleared S1+S2.
  { id: 'q-E5-BOIL-Standard', team: 'Purdue', event: '1v1', robotClass: 'Standard', match: 'E5', time: '8:20', passedStations: ['s1', 's2'], busyAtStation: null, claimedBy: null,            isBackup: false, skipped: false, failed: false, urgent: false },
  // RGVT (E5.blue) — 1v1 Standard claimed at S1.
  { id: 'q-E5-RGVT-Standard', team: 'VT', event: '1v1', robotClass: 'Standard', match: 'E5', time: '8:20', passedStations: [], busyAtStation: 's1', claimedBy: MOCK_OTHER_S1, claimedAt: MOCK_FRESH_CLAIM, isBackup: false, skipped: false, failed: false, urgent: false },
  // RGVT brings a backup Standard — must re-inspect every match.
  { id: 'q-E5-RGVT-Backup',   team: 'VT', event: '1v1', robotClass: 'Standard', match: 'E5', time: '8:20', passedStations: [], busyAtStation: null, claimedBy: null,         isBackup: true,  skipped: false, failed: false, urgent: false },
];

export const initialWarnings: Warning[] = [
  { id: 'W12', match: 'G3', team: 'SFU', code: 'R19', severity: 'minor', note: 'Pit area unattended',     at: '2m ago' },
  { id: 'W11', match: 'G7', team: 'PolyMTL', code: 'R1',  severity: 'dq',    note: 'Late to field x3 — DQ',   at: '14m ago' },
  { id: 'W10', match: 'E2', team: 'PolyMTL', code: 'R7',  severity: 'minor', note: 'Driver swap mid-match',   at: '22m ago' },
  { id: 'W09', match: 'G2', team: 'CU',   code: 'R19', severity: 'minor', at: '38m ago' },
];

// Per-event live-desk inspection rollup. 3v3 and 1v1 are independent; reset
// on match-end. Mock state shows mid-event variety. Only includes teams
// participating in the relevant event; others default to 'pending' implicitly.
export const initialTeamRobotInspection: TeamRobotInspection = {
  UW: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' }, '1v1': { Standard: 'l3' } },
  UoN: { '1v1': { Standard: 'l3' } },
  SFU: { '3v3': { Standard: 'l1', Hero: 'pending', Sentry: 'pending' } },
  Purdue: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' }, '1v1': { Standard: 'l2' } },
  CU:   { '3v3': { Standard: 'l3', Hero: 'l2',      Sentry: 'l1' } },
  ACC: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' } },
  Mac: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' }, '1v1': { Standard: 'l3' } },
  MU: { '3v3': { Standard: 'l1', Hero: 'pending', Sentry: 'pending' } },
  UMN: { '3v3': { Standard: 'l1', Hero: 'failed',  Sentry: 'pending' } },
  NYU: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' } },
  UBC: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' } },
  PolyMTL: { '3v3': { Standard: 'pending', Hero: 'pending', Sentry: 'pending' } },
  QU: { '3v3': { Standard: 'l2', Hero: 'l1',      Sentry: 'pending' } },
  OTU: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' } },
  VT: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' }, '1v1': { Standard: 'pending' } },
  GT: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' } },
  PoliTO: { '3v3': { Standard: 'l3', Hero: 'l2',      Sentry: 'l1' } },
  TAMU: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' } },
  RHIT: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' } },
  UCSD: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' } },
  WashU: { '3v3': { Standard: 'l3', Hero: 'l3',      Sentry: 'l3' } },
  UCSB: { '1v1': { Standard: 'pending' } },
  UTA: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l2' }, '1v1': { Standard: 'pending' } },
  NUU: { '1v1': { Standard: 'l2' } },
};

// Standalone pre-inspection levels — separate from the live desk rollup
// (initialTeamRobotInspection). Persistent across matches, only changed via
// the Pre-Inspection screen / Teams → Robots tab. Mirrors the live rollup at
// app-start mock; in production they diverge over the day.
export const initialPreInspectionLevel: PreInspectionLevel = {
  UW: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' }, '1v1': { Standard: 'l3' }, ec: { Engineer: 'l3' } },
  SFU: { '3v3': { Standard: 'l1', Hero: 'pending', Sentry: 'pending' } },
  Purdue: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' }, '1v1': { Standard: 'l2' } },
  CU:   { '3v3': { Standard: 'l3', Hero: 'l2', Sentry: 'l1' } },
  ACC: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
  Mac: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' }, '1v1': { Standard: 'l3' } },
  MU: { '3v3': { Standard: 'l1', Hero: 'pending', Sentry: 'pending' } },
  UMN: { '3v3': { Standard: 'l1', Hero: 'failed', Sentry: 'pending' } },
  NYU: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
  UBC: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
  PolyMTL: { '3v3': { Standard: 'pending', Hero: 'pending', Sentry: 'pending' } },
  QU: { '3v3': { Standard: 'l2', Hero: 'l1', Sentry: 'pending' } },
  OTU: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
  VT: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' }, '1v1': { Standard: 'pending' } },
  GT: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
  PoliTO: { '3v3': { Standard: 'l2', Hero: 'l2', Sentry: 'l1' } },
  TAMU: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
  RHIT: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
  UCSD: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
  WashU: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
  UoN:  { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
  UCSB: { '1v1': { Standard: 'pending' } },
  UTA: { '1v1': { Standard: 'pending' } },
  NUU: { '1v1': { Standard: 'l2' } },
};

export const initialCheckIn: CheckIn = {
  UW: { spielGiven: true,  badges: 6, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  SFU: { spielGiven: true,  badges: 4, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: false, checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  Purdue: { spielGiven: true,  badges: 7, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  CU:   { spielGiven: true,  badges: 5, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  ACC: { spielGiven: true,  badges: 4, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: false, checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  Mac: { spielGiven: true,  badges: 6, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  MU: { spielGiven: false, badges: 3, badges1v1: false, badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: false, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  UMN: { spielGiven: true,  badges: 4, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: false, checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  NYU: { spielGiven: true,  badges: 5, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  UBC: { spielGiven: true,  badges: 6, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  PolyMTL: { spielGiven: false, badges: 0, badges1v1: false, badges3v3: false, badgesEc: false, borrowing: false, checkInForm: false, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  QU: { spielGiven: true,  badges: 4, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: true },
  OTU: { spielGiven: true,  badges: 5, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: false, checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  VT: { spielGiven: true,  badges: 6, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: true },
  GT: { spielGiven: true,  badges: 4, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: false, checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  PoliTO: { spielGiven: true,  badges: 5, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  TAMU: { spielGiven: true,  badges: 7, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  RHIT: { spielGiven: true,  badges: 4, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: false, checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  UCSD: { spielGiven: true,  badges: 6, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  WashU: { spielGiven: true,  badges: 5, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: true,  checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  UoN:  { spielGiven: true,  badges: 5, badges1v1: true,  badges3v3: true,  badgesEc: false, borrowing: false, checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  UCSB: { spielGiven: true,  badges: 3, badges1v1: true,  badges3v3: false, badgesEc: false, borrowing: true,  checkInForm: false, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  UTA: { spielGiven: true,  badges: 3, badges1v1: true,  badges3v3: false, badgesEc: false, borrowing: false, checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
  NUU: { spielGiven: true,  badges: 4, badges1v1: true,  badges3v3: false, badgesEc: false, borrowing: false, checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false },
};

// Field-calibration seed — looks "mid practice-matches": a few teams fully
// calibrated, some partway, the rest absent (== pending). Only events a team
// actually plays are keyed, so the screen's N/A cells stay coherent. The
// SupabaseStore overwrites this from the field_calibration table on fetchAll.
export const initialFieldCalibration: FieldCalibration = {
  UW:     { '3v3': true,  '1v1': true },
  SFU:    { '3v3': true,  '1v1': false },
  Purdue: { '3v3': true,  '1v1': true },
  CU:     { '3v3': true,  '1v1': false },
  Mac:    { '3v3': true,  '1v1': true },
  UBC:    { '3v3': true,  '1v1': true },
  NYU:    { '3v3': true },
  // 1v1-only teams
  UCSB:   { '1v1': true },
  UTA:    { '1v1': false },
  NUU:    { '1v1': true },
};

// Team-pictures seed — a few teams partway through photo day. Keyed per
// (team, event, robot); robots not listed (and absent events) read as
// nothing-shot in the UI. Only events a team plays are keyed. The
// SupabaseStore overwrites this from the team_pictures table on fetchAll.
export const initialTeamPictures: TeamPictures = {
  UW: {
    '3v3': {
      Standard: { robot: true,  driver: true,  driverName: 'Alex Chen',  driverPronouns: 'he/him',   chief: true,  chiefName: 'Priya Rao',  chiefPronouns: 'she/her' },
      Hero:     { robot: true,  driver: false, driverName: '',           driverPronouns: '',         chief: false, chiefName: '',          chiefPronouns: '' },
      Sentry:   { robot: false, driver: false, driverName: '',           driverPronouns: '',         chief: false, chiefName: '',          chiefPronouns: '' },
    },
    '1v1': {
      Standard: { robot: true,  driver: true,  driverName: 'Alex Chen',  driverPronouns: 'he/him',   chief: false, chiefName: '',          chiefPronouns: '' },
    },
    ec: {
      Engineer: { robot: false, driver: false, driverName: '',           driverPronouns: '',         chief: false, chiefName: '',          chiefPronouns: '' },
    },
  },
  SFU: {
    '3v3': {
      Standard: { robot: true,  driver: true,  driverName: 'Jordan Lee', driverPronouns: 'they/them', chief: true,  chiefName: 'Sam Ortiz',  chiefPronouns: 'he/him' },
      Hero:     { robot: true,  driver: true,  driverName: 'Mei Tan',    driverPronouns: 'she/her',   chief: true,  chiefName: 'Devon Pyle', chiefPronouns: 'he/him' },
    },
  },
};

// Captain's-Meeting starting state — group letters for 3v3 quals + initial
// seeds for 1v1 Swiss Round 1. Derived from config/teams.json's
// `group_3v3` / `initial_seed_1v1` fields, which the seed script (`npm run
// seed:event`) also pushes into Supabase. Single source of truth. The
// admin screen treats state.teamConfig as the runtime source of truth and
// dragging edits it via localStorage drafts.
export const initialTeamConfig: TeamConfig = (() => {
  const config: TeamConfig = {};
  for (const t of teamsConfigData.teams) {
    const entry: { group?: GroupLetter; initialSeed1v1?: number } = {};
    if (t.group_3v3) entry.group = t.group_3v3 as GroupLetter;
    if (t.initial_seed_1v1 != null) entry.initialSeed1v1 = t.initial_seed_1v1;
    if (entry.group != null || entry.initialSeed1v1 != null) {
      config[t.id] = entry;
    }
  }
  return config;
})();

// Per-event Quals-Live flags — all false in the mock so the seeding screen
// starts editable. Toggling either to true (via a Quals Live click) locks
// that event's seeding UI and renders the read-only post-commit view.
export const initialQualsLive: Record<MatchEvent, boolean> = {
  '3v3': false, '1v1': false, 'ec': false,
};

// Per-event Quals-Begun flags. The mock paints a mid-event Day-1 morning
// (live matches, mid-flight queue), which means the practice window is
// over — so both combat events start `true` and Match Control renders the
// qual schedule exactly as before this feature. Tests that exercise the
// Practice panel construct a state with these flipped to false. 'ec' has
// no begin gate and is always true. (Yes, this leaves the slightly odd
// combo begun=true + live=false; the mock's quals predate the Captain's
// Meeting feature and were hand-seeded rather than published.)
export const initialQualsBegun: Record<MatchEvent, boolean> = {
  '3v3': true, '1v1': true, 'ec': true,
};

// Practice matches — none in the mock (the mid-event snapshot is past the
// practice window). The PracticeMatch shape is exercised via tests.
export const initialPracticeMatches: PracticeMatch[] = [];
